<template>
  <div class="fixed inset-0 z-[60] flex items-center justify-center">
    <div class="absolute inset-0 bg-black/50" @click="$emit('close')"></div>
    <div class="relative w-full max-w-lg mx-4 bg-white dark:bg-secondary-800 rounded-xl shadow-2xl overflow-hidden">
      <!-- Header -->
      <div class="flex items-center justify-between px-5 py-4 border-b border-secondary-200 dark:border-secondary-700">
        <div>
          <h3 class="text-base font-semibold text-secondary-900 dark:text-white">
            {{ source.type === 'shipment' ? 'Archivos del embarque' : 'Archivos de la orden de compra' }}
          </h3>
          <p v-if="source.label" class="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">{{ source.label }}</p>
        </div>
        <button @click="$emit('close')" class="p-1.5 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors">
          <X class="w-4 h-4 text-secondary-500" />
        </button>
      </div>

      <!-- Content -->
      <div class="p-4">
        <!-- Loading -->
        <div v-if="isLoading" class="flex flex-col items-center justify-center py-10">
          <div class="w-7 h-7 border-3 border-primary-600 border-t-transparent rounded-full animate-spin mb-3"></div>
          <p class="text-sm text-secondary-500">Cargando archivos...</p>
        </div>

        <!-- Error de carga -->
        <div v-else-if="loadError" class="text-center py-10">
          <AlertCircle class="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p class="text-sm text-secondary-600 dark:text-secondary-300">No se pudieron cargar los archivos</p>
          <p class="text-xs text-secondary-400 mt-1">Cierra y vuelve a intentarlo</p>
        </div>

        <!-- Empty -->
        <div v-else-if="files.length === 0" class="text-center py-10">
          <FolderOpen class="w-10 h-10 text-secondary-300 dark:text-secondary-600 mx-auto mb-3" />
          <p class="text-sm text-secondary-500 dark:text-secondary-400">No hay archivos disponibles</p>
        </div>

        <!-- File list -->
        <div v-else class="space-y-1.5 max-h-72 overflow-y-auto">
          <label
            v-for="file in files"
            :key="file.id"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors"
            :class="selectedIds.has(file.id)
              ? 'bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-300 dark:ring-primary-700'
              : 'hover:bg-secondary-50 dark:hover:bg-secondary-700/50'"
          >
            <input
              type="checkbox"
              :checked="selectedIds.has(file.id)"
              @change="toggleFile(file)"
              class="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
            />
            <FileText class="w-5 h-5 flex-shrink-0" :class="getFileColor(file.fileName)" />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-secondary-900 dark:text-white truncate">{{ getFileDisplayName(file) }}</p>
              <p class="text-xs text-secondary-400">{{ formatSize(file.fileSize) }}</p>
            </div>
          </label>
        </div>

        <!-- Selection summary -->
        <div v-if="files.length > 0" class="mt-3 pt-3 border-t border-secondary-100 dark:border-secondary-700 flex items-center justify-between text-xs text-secondary-500 dark:text-secondary-400">
          <span>{{ selectedIds.size }} archivo(s) seleccionado(s)</span>
          <span :class="totalSelectedSize > MAX_TOTAL_SIZE ? 'text-red-500 font-semibold' : ''">
            {{ formatSize(totalSelectedSize) }} / {{ formatSize(MAX_TOTAL_SIZE) }}
          </span>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-2 px-5 py-3 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900/50">
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-sm text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-lg transition-colors"
        >
          Cancelar
        </button>
        <button
          @click="confirmSelection"
          :disabled="selectedIds.size === 0 || isAttaching || hasValidationError"
          class="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <span v-if="isAttaching">Adjuntando...</span>
          <span v-else>Adjuntar ({{ selectedIds.size }})</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { X, FileText, FolderOpen, AlertCircle } from 'lucide-vue-next'
import { getStorage, ref as storageRef, getBlob } from 'firebase/storage'
import { useCompanyStore } from '@/stores/company'
import { ShipmentService } from '@/application/services/ShipmentService'
import { FirestoreShipmentRepository } from '@/infrastructure/repositories/FirestoreShipmentRepository'
import { usePurchaseOrderDocuments } from '@/composables/usePurchaseOrderDocuments'
import { EMAIL_ATTACHMENT_TOTAL_LIMIT_BYTES } from '@/config/emailAttachmentLimits'
import { getFileDisplayName, getFileDownloadName } from '@/utils/shipmentFileDisplayName'

export interface AttachmentSource {
  type: 'shipment' | 'purchaseOrder'
  id: string
  label?: string
}

interface DocumentFile {
  id: string
  fileName: string
  /** Nombre visual; solo lo traen los documentos de embarque */
  displayName?: string
  fileUrl: string
  fileSize: number
  mimeType: string
  storagePath?: string
}

// Solo se limita el peso total del correo, nunca el de cada archivo ni la cantidad
const MAX_TOTAL_SIZE = EMAIL_ATTACHMENT_TOTAL_LIMIT_BYTES

const props = defineProps<{
  source: AttachmentSource
  currentAttachmentsSize?: number
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'attach', files: File[], failed: string[]): void
}>()

const companyStore = useCompanyStore()
const files = ref<DocumentFile[]>([])
const selectedIds = ref<Set<string>>(new Set())
const isLoading = ref(false)
const isAttaching = ref(false)
const loadError = ref(false)

const totalSelectedSize = computed(() => {
  let size = props.currentAttachmentsSize || 0
  for (const file of files.value) {
    if (selectedIds.value.has(file.id)) {
      size += file.fileSize
    }
  }
  return size
})

const hasValidationError = computed(() => totalSelectedSize.value > MAX_TOTAL_SIZE)

function toggleFile(file: DocumentFile) {
  const newSet = new Set(selectedIds.value)
  if (newSet.has(file.id)) {
    newSet.delete(file.id)
  } else {
    newSet.add(file.id)
  }
  selectedIds.value = newSet
}

function getFileColor(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  if (ext === 'pdf') return 'text-red-500'
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'text-green-600'
  if (['doc', 'docx'].includes(ext)) return 'text-blue-500'
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'text-purple-500'
  return 'text-secondary-400'
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

async function loadShipmentFiles() {
  const companyId = companyStore.selectedCompanyId
  if (!companyId) return
  const service = new ShipmentService(new FirestoreShipmentRepository())
  const result = await service.getShipmentFiles(props.source.id, companyId)
  files.value = result.map(f => ({
    id: f.id,
    fileName: f.fileName,
    displayName: f.displayName,
    fileUrl: f.fileUrl,
    fileSize: f.fileSize,
    mimeType: f.mimeType,
  }))
}

async function loadPurchaseOrderFiles() {
  const { documents, loadDocuments } = usePurchaseOrderDocuments()
  await loadDocuments(props.source.id)
  files.value = documents.value.map(d => ({
    id: d.id,
    fileName: d.fileName,
    fileUrl: d.fileUrl,
    fileSize: d.fileSize,
    mimeType: d.mimeType,
    storagePath: d.storagePath,
  }))
}

async function downloadViaStorageSDK(doc: DocumentFile): Promise<Blob> {
  // Si hay storagePath válido, usar Storage SDK
  if (doc.storagePath) {
    const storage = getStorage()
    const fileRef = storageRef(storage, doc.storagePath)
    return await getBlob(fileRef)
  }
  // Si solo tenemos fileUrl, descargar directamente via fetch
  const response = await fetch(doc.fileUrl)
  if (!response.ok) {
    throw new Error(`Error descargando archivo: ${response.status} ${response.statusText}`)
  }
  return await response.blob()
}

async function confirmSelection() {
  isAttaching.value = true
  try {
    const selected = files.value.filter(f => selectedIds.value.has(f.id))
    const fileObjects: File[] = []
    const failed: string[] = []

    for (const doc of selected) {
      try {
        const blob = await downloadViaStorageSDK(doc)
        const file = new window.File([blob], getFileDownloadName(doc), { type: doc.mimeType || blob.type })
        fileObjects.push(file)
      } catch (err) {
        console.error(`Error descargando ${doc.fileName}:`, err)
        failed.push(getFileDisplayName(doc))
      }
    }

    // Siempre se emite: el compositor notifica tanto los adjuntados como los que fallaron
    emit('attach', fileObjects, failed)
  } finally {
    isAttaching.value = false
  }
}

onMounted(async () => {
  isLoading.value = true
  try {
    if (props.source.type === 'shipment') {
      await loadShipmentFiles()
    } else {
      await loadPurchaseOrderFiles()
    }
  } catch (err) {
    console.error('Error cargando archivos:', err)
    loadError.value = true
  } finally {
    isLoading.value = false
  }
})
</script>
